(() => {
  const script = document.currentScript || document.querySelector('script[data-maxsports-navigation]');
  if (!script || document.documentElement.hasAttribute('data-maxsports-navigation-ready')) return;
  document.documentElement.setAttribute('data-maxsports-navigation-ready', 'true');

  const rootPath = new URL(script.dataset.rootUrl || '/', location.origin).pathname.replace(/\/?$/, '/');
  const storageKey = 'maxsports.navigation.v1';
  const lifetime = 30 * 60 * 1000;
  const returnHash = '#maxsports-return';
  const sortValues = new Set(['manual', 'best-selling', 'title-ascending', 'title-descending', 'price-ascending', 'price-descending', 'created-ascending', 'created-descending', 'relevance']);
  const flexValues = new Set(['hi-flex', 'medium', 'stiff', 'extra-stiff', 'not-provided']);
  const state = { version: 1, routes: [], contexts: [] };
  let trackedRoute = null;
  let lastSnapshot = null;
  let pendingRestore = null;
  let restoreFrame = 0;
  let scrollFrame = 0;
  let stopped = false;

  function publicUrl(value, kind) {
    try {
      const url = new URL(value, location.origin);
      if (url.origin !== location.origin || url.username || url.password || url.href.length > 4096) return null;
      if (!url.pathname.startsWith(rootPath)) return null;
      const localPath = url.pathname.slice(rootPath.length);
      if (kind === 'collection' && !/^collections\/[a-zA-Z0-9_-]+(?:\/[a-zA-Z0-9_%+.-]+)?\/?$/.test(localPath)) return null;
      if (kind === 'product' && !/^(?:collections\/[a-zA-Z0-9_-]+\/)?products\/[a-zA-Z0-9_-]+\/?$/.test(localPath)) return null;
      if (kind === 'product') return url.pathname.replace(/\/$/, '');
      const params = new URLSearchParams();
      for (const [key, value] of url.searchParams) {
        if (value.length > 160 || /[\u0000-\u001f\u007f]/.test(value)) continue;
        if (/^filter\.(p|v)\.[^\u0000-\u001f\u007f&=?#]{1,120}$/u.test(key)) params.append(key, value);
        else if (key === 'sort_by' && sortValues.has(value)) params.append(key, value);
        else if (key === 'ms_flex' && flexValues.has(value)) params.append(key, value);
        else if (key === 'page' && /^[1-9]\d{0,4}$/.test(value) && Number(value) <= 25000) params.append(key, value);
        else if (key === 'preview_theme_id' && /^\d{1,20}$/.test(value)) params.append(key, value);
        else if (key === 'pb' && /^[01]$/.test(value)) params.append(key, value);
        else if (key === 'view' && /^[a-zA-Z0-9_-]{1,40}$/.test(value)) params.append(key, value);
      }
      const relative = url.pathname.replace(/\/$/, '') + (params.size ? '?' + params.toString() : '');
      return relative.length <= 2048 ? relative : null;
    } catch (_) {
      return null;
    }
  }

  function fresh(at) {
    return Number.isFinite(at) && at <= Date.now() + 60000 && at > Date.now() - lifetime;
  }

  function sanitizeRoute(record) {
    if (!record || !fresh(record.at) || publicUrl(record.url, 'collection') !== record.url) return null;
    if (!Number.isFinite(record.x) || !Number.isFinite(record.y) || record.x < 0 || record.y < 0 || record.x > 1000000 || record.y > 1000000) return null;
    return {
      url: record.url,
      at: record.at,
      x: record.x,
      y: record.y,
      anchor: publicUrl(record.anchor, 'product'),
      offset: Number.isFinite(record.offset) && Math.abs(record.offset) < 10000 ? record.offset : 0,
      focus: publicUrl(record.focus, 'product'),
    };
  }

  function prune() {
    state.routes = state.routes.map(sanitizeRoute).filter(Boolean).sort((a, b) => b.at - a.at).slice(0, 30);
    state.contexts = state.contexts.filter((record) => record && fresh(record.at)
      && publicUrl(record.product, 'product') === record.product
      && publicUrl(record.collection, 'collection') === record.collection).sort((a, b) => b.at - a.at).slice(0, 12)
      .map(({ product, collection, at }) => ({ product, collection, at }));
  }

  function persist() {
    prune();
    try {
      sessionStorage.setItem(storageKey, JSON.stringify(state));
    } catch (_) {}
  }

  function readStorage() {
    try {
      const raw = sessionStorage.getItem(storageKey);
      if (!raw || raw.length > 40000) return;
      const parsed = JSON.parse(raw);
      if (parsed.version !== 1 || !Array.isArray(parsed.routes) || !Array.isArray(parsed.contexts)) return;
      state.routes = parsed.routes;
      state.contexts = parsed.contexts;
      prune();
    } catch (_) {}
  }

  function productLinks() {
    return Array.from(document.querySelectorAll('#ProductGridContainer #product-grid a[href]')).filter((link) => publicUrl(link.href, 'product'));
  }

  function gridBusy() {
    return Boolean(document.querySelector('#ProductGridContainer .collection.loading, #ProductGridContainer[data-ms-filter-loading="true"], #ProductGridContainer [data-ms-filter-loading="true"]'));
  }

  function snapshot(route = trackedRoute, focus = null) {
    if (!route || gridBusy() || pendingRestore) return lastSnapshot;
    const cards = Array.from(document.querySelectorAll('#ProductGridContainer #product-grid > li'));
    const card = cards.find((element) => {
      const rect = element.getBoundingClientRect();
      return rect.bottom > 0 && rect.top < innerHeight;
    });
    const link = card && Array.from(card.querySelectorAll('a[href]')).find((element) => publicUrl(element.href, 'product'));
    lastSnapshot = {
      url: route,
      at: Date.now(),
      x: Math.max(0, scrollX),
      y: Math.max(0, scrollY),
      anchor: link ? publicUrl(link.href, 'product') : null,
      offset: card ? card.getBoundingClientRect().top : 0,
      focus: focus || (lastSnapshot && lastSnapshot.url === route ? lastSnapshot.focus : null),
    };
    return lastSnapshot;
  }

  function save(record = snapshot()) {
    const valid = sanitizeRoute(record);
    if (!valid) return;
    state.routes = state.routes.filter((item) => item.url !== valid.url);
    state.routes.unshift(valid);
    persist();
  }

  function stopRestore() {
    pendingRestore = null;
    if (restoreFrame) cancelAnimationFrame(restoreFrame);
    restoreFrame = 0;
  }

  function applyRestore(record) {
    const link = record.anchor && productLinks().find((element) => publicUrl(element.href, 'product') === record.anchor);
    const card = link && link.closest('#product-grid > li');
    let y = record.y;
    if (card) y = scrollY + card.getBoundingClientRect().top - record.offset;
    const maxY = Math.max(0, document.documentElement.scrollHeight - innerHeight);
    window.scrollTo({ left: Math.min(record.x, Math.max(0, document.documentElement.scrollWidth - innerWidth)), top: Math.max(0, Math.min(y, maxY)), behavior: 'instant' });
  }

  function runRestore() {
    restoreFrame = 0;
    if (!pendingRestore || stopped) return;
    if (publicUrl(location.href, 'collection') !== pendingRestore.record.url || Date.now() > pendingRestore.deadline) {
      stopRestore();
      return;
    }
    if (!gridBusy() && document.getElementById('product-grid')) {
      pendingRestore.frames += 1;
      if (pendingRestore.frames >= 2) {
        applyRestore(pendingRestore.record);
        if (document.readyState === 'complete') {
          const record = pendingRestore.record;
          stopRestore();
          const focus = record.focus && productLinks().find((element) => publicUrl(element.href, 'product') === record.focus
            && !element.closest('[hidden], [inert]') && element.getClientRects().length > 0);
          if (focus) focus.focus({ preventScroll: true });
          snapshot();
          return;
        }
      }
    } else {
      pendingRestore.frames = 0;
    }
    restoreFrame = requestAnimationFrame(runRestore);
  }

  function startRestore(route) {
    stopRestore();
    const record = state.routes.find((item) => item.url === route && fresh(item.at));
    if (!record) return;
    pendingRestore = { record, deadline: Date.now() + 30000, frames: 0 };
    restoreFrame = requestAnimationFrame(runRestore);
  }

  function updateRoute() {
    const route = publicUrl(location.href, 'collection');
    if (route !== trackedRoute) {
      trackedRoute = route;
      lastSnapshot = null;
    }
  }

  function addReturnLink() {
    const product = publicUrl(location.href, 'product');
    if (!product) return;
    const referrerCollection = publicUrl(document.referrer, 'collection');
    const referrerProduct = publicUrl(document.referrer, 'product');
    const context = state.contexts.find((record) => record.product === product
      && fresh(record.at) && (record.collection === referrerCollection || referrerProduct === product));
    const collection = context ? context.collection : referrerCollection;
    const breadcrumb = document.querySelector('main .maxsports-breadcrumbs');
    if (!collection || !breadcrumb || !script.dataset.returnLabel) return;
    const link = document.createElement('a');
    link.className = 'maxsports-results-return link underlined-link';
    link.dataset.maxsportsReturnLink = 'true';
    if (script.dataset.returnAria) link.setAttribute('aria-label', script.dataset.returnAria);
    link.href = collection + (state.routes.some((record) => record.url === collection && fresh(record.at)) ? returnHash : '');
    const arrow = document.createElement('span');
    arrow.setAttribute('aria-hidden', 'true');
    arrow.textContent = '← ';
    link.append(arrow, document.createTextNode(script.dataset.returnLabel));
    breadcrumb.prepend(link);
  }

  function init() {
    readStorage();
    trackedRoute = publicUrl(location.href, 'collection');
    addReturnLink();
    if (!trackedRoute) return;
    const explicitReturn = location.hash === returnHash;
    if (explicitReturn) history.replaceState(history.state, '', location.pathname + location.search);
    const navigation = performance.getEntriesByType ? performance.getEntriesByType('navigation')[0] : null;
    if (explicitReturn || (navigation && navigation.type === 'back_forward')) startRestore(trackedRoute);
    else snapshot();

    document.addEventListener('click', (event) => {
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const target = event.target instanceof Element ? event.target.closest('a[href]') : null;
      if (!target || !target.closest('#ProductGridContainer #product-grid')) return;
      const product = publicUrl(target.href, 'product');
      const route = publicUrl(location.href, 'collection');
      if (!product || !route || event.defaultPrevented) return;
      updateRoute();
      save(snapshot(route, product));
      state.contexts = state.contexts.filter((record) => record.product !== product);
      state.contexts.unshift({ product, collection: route, at: Date.now() });
      persist();
    }, true);

    document.addEventListener('input', (event) => {
      if (event.target instanceof Element && event.target.closest('facet-filters-form')) save();
    }, true);
    document.addEventListener('click', (event) => {
      if (event.target instanceof Element && event.target.closest('facet-remove, .pagination a')) save();
    }, true);
    window.addEventListener('scroll', () => {
      if (pendingRestore || scrollFrame) return;
      scrollFrame = requestAnimationFrame(() => {
        scrollFrame = 0;
        updateRoute();
        snapshot();
      });
    }, { passive: true });
    window.addEventListener('pagehide', () => {
      if (pendingRestore) save(lastSnapshot);
      else save();
      stopped = true;
      stopRestore();
    });
    window.addEventListener('pageshow', (event) => {
      stopped = false;
      if (event.persisted) {
        stopRestore();
        updateRoute();
        snapshot();
      }
    });
    window.addEventListener('popstate', () => {
      save(lastSnapshot);
      updateRoute();
      startRestore(trackedRoute);
    });
    for (const type of ['wheel', 'touchstart', 'pointerdown', 'keydown']) {
      window.addEventListener(type, () => stopRestore(), { passive: true });
    }
    const grid = document.getElementById('ProductGridContainer');
    if (grid) new MutationObserver(() => {
      updateRoute();
      if (!pendingRestore) snapshot();
    }).observe(grid, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'data-ms-filter-loading'] });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
